// ═══════════════════════════════════════════════════════════════════
//  TOQUM · QUALITY DASHBOARD
//  Time-range driven, market-style charts. Same filter mechanism as
//  Nirvikalp Dashboard.js (season/brand/region/supplier/product) with
//  a date range and granularity added on top — every KPI, series and
//  table recomputes from the selected window.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, Brush, ReferenceLine,
} from 'recharts';
import {
  T, CC, num, today, addDays, Page, CardRow, Split, FSel, StatCard, Section, Tag, Badge,
  DataTable, Btn, RANGE_PRESETS, bucketOf, trendOf, inRange,
} from './Shared';
import { KPI_CATALOG } from './Team';

const fmtDate = d => d ? d.slice(5) : '';
const pct = v => v === null || v === undefined ? '—' : `${v}%`;

// Sparkline-style trend badge (the "market" feel)
const Trend = ({ value, invert = false, suffix = '%' }) => {
  if (value === null || value === undefined) return <span style={{ color:T.textSub, fontSize:11 }}>—</span>;
  const good = invert ? value < 0 : value > 0;
  const flat = Math.abs(value) < 0.05;
  const c = flat ? T.textSub : good ? T.success : T.danger;
  return (
    <span style={{ color:c, fontWeight:800, fontSize:11.5 }}>
      {flat ? '▬' : value > 0 ? '▲' : '▼'} {Math.abs(value)}{suffix}
    </span>
  );
};

const KpiTile = ({ label, value, sub, trend, invert, suffix = '%', color = T.primary }) => (
  <div style={{ background:'white', border:`1px solid ${T.border}`, borderLeft:`4px solid ${color}`,
    borderRadius:10, padding:'13px 16px', boxShadow:T.cardShadow }}>
    <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', color:T.textSub,
      letterSpacing:'0.05em' }}>{label}</div>
    <div style={{ display:'flex', alignItems:'baseline', gap:8, margin:'4px 0 2px' }}>
      <span style={{ fontSize:24, fontWeight:900, color:T.textMain, lineHeight:1 }}>{value}</span>
      {trend !== undefined && <Trend value={trend} invert={invert} suffix={suffix} />}
    </div>
    {sub && <div style={{ fontSize:10.5, color:T.textSub }}>{sub}</div>}
  </div>
);

export default function QualityDashboard({ db, currentUser }) {
  // ─── FILTERS ───
  const [range, setRange] = useState('3M');
  const [from, setFrom] = useState(RANGE_PRESETS['3M']()[0]);
  const [to, setTo]     = useState(RANGE_PRESETS['3M']()[1]);
  const [gran, setGran] = useState('week');
  const [flt, setFlt]   = useState({ season:'All', brand:'All', region:'All', supplier:'All', product:'All', qa:'All' });
  // Output must be measured at ONE stage. Summing every stage counts each
  // piece once per stage it passes through, inflating the figure several times.
  const [outStage, setOutStage] = useState('Packing');
  const setF = (k,v) => setFlt(p=>({ ...p, [k]:v }));
  const resetAll = () => {
    setFlt({ season:'All', brand:'All', region:'All', supplier:'All', product:'All', qa:'All' });
    applyPreset('3M');
  };
  const applyPreset = (key) => {
    const [f2,t2] = RANGE_PRESETS[key]();
    setRange(key); setFrom(f2); setTo(t2);
    setGran(key==='7D' ? 'day' : ['1M','3M'].includes(key) ? 'week' : 'month');
  };

  const opts = useMemo(() => ({
    season:   [...new Set((db.styles||[]).map(s=>s.season).filter(Boolean))],
    brand:    (db.brands||[]).map(b=>b.name||b),
    region:   (db.regions||[]).map(r=>r.name||r),
    supplier: (db.suppliers||[]).map(s=>s.name),
    product:  [...new Set((db.styles||[]).map(s=>s.product).filter(Boolean))],
    qa:       [...new Set((db.inspections||[]).map(i=>i.qaMember).filter(Boolean))],
  }), [db]);

  // ─── FILTERED DATA WITHIN THE WINDOW ───
  const D = useMemo(() => {
    const supOf   = name => (db.suppliers||[]).find(s=>s.name===name) || {};
    const styleOf = no   => (db.styles||[]).find(s=>s.styleNo===no) || {};
    const poOf    = no   => (db.pos||[]).find(p=>p.poNo===no) || {};

    const matchSup = name => !name ||
      ((flt.supplier==='All' || name===flt.supplier) &&
       (flt.region==='All'   || supOf(name).region===flt.region));

    const matchPO = po => {
      const st = styleOf(po.styleNo);
      return (flt.brand==='All'    || po.brand===flt.brand)
          && (flt.season==='All'   || st.season===flt.season)
          && (flt.product==='All'  || st.product===flt.product)
          && matchSup(po.supplier);
    };
    const poSet = new Set((db.pos||[]).filter(matchPO).map(p=>p.poNo));

    const ctx = (rec) => {
      const po = rec.poNo ? poOf(rec.poNo) : {};
      const st = styleOf(rec.styleNo || po.styleNo);
      return { brand: rec.brand || po.brand, season: st.season, product: st.product,
               supplier: rec.supplier || po.supplier };
    };
    const passFilters = (rec, dateField) => {
      if (!inRange(rec[dateField], from, to)) return false;
      const c = ctx(rec);
      if (flt.brand!=='All'   && c.brand   !== flt.brand)   return false;
      if (flt.season!=='All'  && c.season  !== flt.season)  return false;
      if (flt.product!=='All' && c.product !== flt.product) return false;
      if (!matchSup(c.supplier)) return false;
      if (flt.qa!=='All' && rec.qaMember && rec.qaMember !== flt.qa) return false;
      return true;
    };

    return {
      inspections: (db.inspections||[]).filter(r=>passFilters(r,'date')),
      exceptions:  (db.exceptions||[]) .filter(r=>passFilters(r,'raisedOn')),
      audits:      (db.audits||[])     .filter(r=>inRange(r.auditDate,from,to) && matchSup(r.supplierName)),
      caps:        (db.caps||[])       .filter(r=>inRange(r.createdOn,from,to) && matchSup(r.supplier)),
      claims:      (db.claims||[])     .filter(r=>inRange(r.raisedOn,from,to) &&
                     (matchSup(r.complainant)||matchSup(r.respondent))),
      complaints:  (db.customerComplaints||[]).filter(r=>passFilters(r,'date')),
      visits:      (db.visitPlans||[]) .filter(r=>inRange(r.date,from,to) && matchSup(r.supplier)),
      production:  (db.productionLogs||[]).filter(r=>inRange(r.date,from,to) && poSet.has(r.poNo)),
      warnings:    (db.warningLetters||[]).filter(r=>inRange(r.date,from,to) && matchSup(r.supplier)),
      samples:     (db.sampleEvaluations||[]).filter(r=>passFilters(r,'date')),
      plans:       (db.qualityPlans||[]).filter(q=>poSet.has(q.poNo)),
      suppliers:   (db.suppliers||[]).filter(s=>matchSup(s.name)),
      commercialDecisions: (db.commercialDecisions||[]).filter(r=>inRange(r.raisedOn,from,to) && matchSup(r.supplier)),
      poSet,
    };
  }, [db.inspections, db.exceptions, db.commercialDecisions, db.audits, db.caps, db.claims, db.customerComplaints, db.visitPlans, db.productionLogs, db.warningLetters, db.sampleEvaluations, db.qualityPlans, db.suppliers, db.pos, flt, from, to]);

  // ─── TIME SERIES ───
  const series = useMemo(() => {
    const buckets = {};
    const touch = b => (buckets[b] = buckets[b] || { date:b, inspections:0, passed:0, failed:0,
      critical:0, major:0, minor:0, checked:0, produced:0, exceptions:0, complaints:0,
      claimAmount:0, auditSum:0, auditCount:0 });

    D.inspections.forEach(i => {
      const b = touch(bucketOf(i.date, gran));
      b.inspections++;
      if (i.result==='Passed') b.passed++;
      if (i.result==='Failed') b.failed++;
      b.critical += num(i.totals?.critical);
      b.major    += num(i.totals?.major);
      b.minor    += num(i.totals?.minor);
      b.checked  += num(i.header?.visualCheckQty);
    });
    D.production.filter(l => l.stage === outStage)
      .forEach(l => { touch(bucketOf(l.date, gran)).produced += num(l.qty); });
    D.exceptions.forEach(e => { touch(bucketOf(e.raisedOn, gran)).exceptions++; });
    D.complaints.forEach(c => { touch(bucketOf(c.date, gran)).complaints++; });
    D.claims.forEach(c => { touch(bucketOf(c.raisedOn, gran)).claimAmount += num(c.lossAmount); });
    D.audits.forEach(a => { const b = touch(bucketOf(a.auditDate, gran));
      b.auditSum += num(a.totalScore); b.auditCount++; });

    return Object.values(buckets)
      .sort((a,b)=>a.date.localeCompare(b.date))
      .map(b => ({
        ...b,
        passRate: b.inspections ? +(b.passed/b.inspections*100).toFixed(1) : null,
        // DHU = defects per hundred units. Numerator counts defects, denominator
        // counts units — different things, so this is a rate, never a percentage.
        dhu: b.checked ? +((b.critical+b.major+b.minor)/b.checked*100).toFixed(2) : null,
        defects: b.critical + b.major + b.minor,
        auditScore: b.auditCount ? +(b.auditSum/b.auditCount).toFixed(1) : null,
        label: fmtDate(b.date),
      }));
  }, [D, gran, outStage]);

  // ─── HEADLINE KPIs ───
  const K = useMemo(() => {
    const done   = D.inspections.filter(i=>['Passed','Failed'].includes(i.result));
    const passed = done.filter(i=>i.result==='Passed');
    const defects = D.inspections.reduce((a,i)=>a+num(i.totals?.critical)+num(i.totals?.major)+num(i.totals?.minor),0);
    const checked = D.inspections.reduce((a,i)=>a+num(i.header?.visualCheckQty),0);
    const gp = { A:4, B:3, C:2, D:1 };
    const stageOut = D.production.filter(l=>l.stage===outStage)
      .reduce((a,l)=>a+num(l.qty),0);
    const stageTotals = {};
    D.production.forEach(l => { stageTotals[l.stage] = (stageTotals[l.stage]||0) + num(l.qty); });
    return {
      inspDone: done.length,
      inspSched: D.inspections.filter(i=>i.result==='Pending'||i.status==='Scheduled').length,
      passRate: done.length ? +(passed.length/done.length*100).toFixed(1) : 0,
      dhu: checked ? +(defects/checked*100).toFixed(2) : 0,
      defects, checked,
      produced: stageOut, stageTotals,
      coverage: stageOut ? +(checked/stageOut*100).toFixed(1) : null,
      excRaised: D.exceptions.length,
      excApproved: D.exceptions.filter(e=>e.status==='Approved').length,
      excPending: D.exceptions.filter(e=>e.status==='Pending Approval').length,
      cdTotal:    D.commercialDecisions.length,
      cdPending:  D.commercialDecisions.filter(c=>['Pending QA Manager','On Hold'].includes(c.status)).length,
      cdPassed:   D.commercialDecisions.filter(c=>c.status==='Passed (CD)').length,
      cdEscalated:D.commercialDecisions.filter(c=>c.status==='Escalated to Merchant').length,
      cdValue:    D.commercialDecisions.filter(c=>c.status==='Passed (CD)')
                   .reduce((a,c)=>a+parseFloat(c.orderValue||0),0),
      capTotal: D.caps.length,
      capClosure: D.caps.length ? Math.round(D.caps.filter(c=>c.status==='Closed').length/D.caps.length*100) : 0,
      auditAvg: D.audits.length ? +(D.audits.reduce((a,x)=>a+num(x.totalScore),0)/D.audits.length).toFixed(1) : null,
      auditGrade: D.audits.length ? +(D.audits.reduce((a,x)=>a+(gp[x.grade]||0),0)/D.audits.length).toFixed(2) : null,
      claimsOpen: D.claims.filter(c=>!['Settled','Closed'].includes(c.status)).length,
      claimsSettled: D.claims.filter(c=>c.status==='Settled').length,
      claimLoss: D.claims.reduce((a,c)=>a+num(c.lossAmount),0),
      complaints: D.complaints.length,
      complaintClaim: D.complaints.reduce((a,c)=>a+num(c.claimAmount),0),
      visitsDone: D.visits.filter(v=>v.status==='Completed').length,
      visitsPlanned: D.visits.length,
      warnings: D.warnings.length,
      suppliers: D.suppliers.length,
      overdueSteps: D.plans.flatMap(p=>p.steps).filter(s=>s.mandatory &&
        s.scheduledDate < today() && !['Passed','Approved'].includes(s.status)).length,
      samplesPassed: D.samples.filter(s=>s.verdict==='Approved').length,
      samplesTotal: D.samples.length,
    };
  }, [D, outStage]);

  const trends = {
    passRate: trendOf(series, 'passRate'),
    dhu: trendOf(series, 'dhu'),
    defects: trendOf(series, 'defects'),
    produced: trendOf(series, 'produced'),
    exceptions: trendOf(series, 'exceptions'),
  };

  // ─── SUPPLIER LEAGUE TABLE ───
  const supplierPerf = useMemo(() => D.suppliers.map(s => {
    const insp = D.inspections.filter(i=>i.supplier===s.name && ['Passed','Failed'].includes(i.result));
    const pass = insp.filter(i=>i.result==='Passed').length;
    const maj = D.inspections.filter(i=>i.supplier===s.name)
      .reduce((a,i)=>a+num(i.totals?.critical)+num(i.totals?.major),0);
    const aud = D.audits.filter(a=>a.supplierName===s.name);
    const cps = D.caps.filter(c=>c.supplier===s.name);
    const cmp = D.complaints.filter(c=>c.supplier===s.name);
    return {
      id: s.name, name: s.name.length>14 ? s.name.slice(0,13)+'…' : s.name, full: s.name,
      inspections: insp.length,
      passRate: insp.length ? +(pass/insp.length*100).toFixed(0) : 0,
      majorDefects: maj,
      exceptions: D.exceptions.filter(e=>e.supplier===s.name).length,
      auditScore: aud.length ? +(aud.reduce((a,x)=>a+num(x.totalScore),0)/aud.length).toFixed(1) : null,
      capOpen: cps.filter(c=>c.status!=='Closed').length,
      complaints: cmp.length,
      claimAmount: cmp.reduce((a,c)=>a+num(c.claimAmount),0),
      produced: D.production.filter(l=>l.supplier===s.name && l.stage===outStage)
        .reduce((a,l)=>a+num(l.qty),0),
    };
  }).filter(x=>x.inspections||x.majorDefects||x.exceptions||x.produced||x.complaints), [D, outStage]);

  const teamPerf = useMemo(() => {
    const members = [...new Set(D.inspections.map(i=>i.qaMember).filter(Boolean))];
    return members.map(m => {
      const mine = D.inspections.filter(i=>i.qaMember===m);
      const done = mine.filter(i=>['Passed','Failed'].includes(i.result));
      return { name: m.length>13 ? m.slice(0,12)+'…' : m, full:m, done:done.length,
        passed: done.filter(i=>i.result==='Passed').length,
        exceptions: D.exceptions.filter(e=>e.raisedBy===m).length,
        visits: D.visits.filter(v=>v.member===m && v.status==='Completed').length };
    }).sort((a,b)=>b.done-a.done);
  }, [D]);

  const defectMix = useMemo(() => {
    const mix = {};
    D.inspections.forEach(i => (i.defects||[]).forEach(d => {
      const key = (d.code || d.description || 'Unspecified').split('—')[0].trim() || 'Unspecified';
      mix[key] = (mix[key]||0) + num(d.critical) + num(d.major) + num(d.minor);
    }));
    return Object.entries(mix).map(([name,value])=>({ name, value }))
      .sort((a,b)=>b.value-a.value).slice(0,8);
  }, [D]);

  const resultPie = [
    { name:'Passed', value:D.inspections.filter(i=>i.result==='Passed').length },
    { name:'Failed', value:D.inspections.filter(i=>i.result==='Failed').length },
    { name:'Scheduled', value:D.inspections.filter(i=>i.result==='Pending'||i.status==='Scheduled').length },
  ].filter(x=>x.value);

  const myKpis = (db.kpiPrefs||[]).find(k=>k.member===currentUser.name)?.kpis || [];
  const kpiValue = kpi => ({
    'Inspections Planned vs Done': `${K.inspSched} / ${K.inspDone}`,
    'First-Pass Rate %': `${K.passRate}%`,
    'DHU (Defects per Hundred Units)': K.dhu,   // rate, not a percentage
    'Exceptions Raised': K.excRaised,
    'Exceptions Approved': K.excApproved,
    'Commercial Decisions': K.cdTotal,
    'CD Pending': K.cdPending,
    'CD Passed': K.cdPassed,
    'CD Order Value': K.cdValue ? `₹${(K.cdValue/100000).toFixed(1)}L` : '₹0',
    'Factory Visits Completed': `${K.visitsDone}/${K.visitsPlanned}`,
    'CAP Closure Rate %': `${K.capClosure}%`,
    'Supplier Audit Grade Avg': K.auditGrade ?? '—',
    'Major Defect Suppliers': supplierPerf.filter(s=>s.majorDefects>0).length,
    'Inspection Pass Rate %': `${K.passRate}%`,
    'Pending Follow-ups': (db.reminders||[]).filter(r=>r.member===currentUser.name&&r.status==='Open').length,
    'Claims Open vs Settled': `${K.claimsOpen} / ${K.claimsSettled}`,
    'On-time Inspection Offer %': K.overdueSteps===0 ? '100%' : `${Math.max(0,100-K.overdueSteps*5)}%`,
    'Warning Letters Issued': K.warnings,
    'Blacklisted Suppliers': (db.blacklist||[]).filter(b=>b.status==='Blacklisted').length,
    'Customer Complaints': K.complaints,
  }[kpi] ?? '—');

  const hasData = series.length > 0;
  const axis = { fontSize:10, fill:T.textSub };
  const tooltipStyle = { fontSize:11.5, borderRadius:8, border:`1px solid ${T.border}` };

  return (
    <Page>
      {/* ─── CONTROL BAR ─── */}
      <div style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:12,
        padding:'12px 18px', marginBottom:18, boxShadow:T.cardShadow }}>
        <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', marginBottom:8 }}>
          <div style={{ fontWeight:900, fontSize:16, marginRight:4 }}>🛡️ Quality Dashboard</div>
          <div style={{ display:'flex', gap:3, background:'#f1f5f9', padding:3, borderRadius:8 }}>
            {Object.keys(RANGE_PRESETS).map(k=>(
              <button key={k} onClick={()=>applyPreset(k)}
                style={{ padding:'5px 11px', border:'none', borderRadius:6, cursor:'pointer',
                  fontSize:11, fontWeight:800, letterSpacing:'0.02em',
                  background: range===k ? T.textMain : 'transparent',
                  color: range===k ? 'white' : T.textSub }}>{k}</button>
            ))}
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            <input type="date" value={from} onChange={e=>{ setFrom(e.target.value); setRange('CUSTOM'); }}
              style={{ padding:'5px 8px', border:`1.5px solid ${T.border}`, borderRadius:6, fontSize:11.5 }} />
            <span style={{ color:T.textSub, fontSize:11 }}>→</span>
            <input type="date" value={to} onChange={e=>{ setTo(e.target.value); setRange('CUSTOM'); }}
              style={{ padding:'5px 8px', border:`1.5px solid ${T.border}`, borderRadius:6, fontSize:11.5 }} />
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            <span style={{ fontSize:9, fontWeight:800, color:T.textSub, textTransform:'uppercase' }}>Output at</span>
            <select value={outStage} onChange={e=>setOutStage(e.target.value)}
              style={{ padding:'5px 8px', borderRadius:6, border:`1.5px solid ${T.border}`,
                fontSize:11.5, fontWeight:700, background:'white' }}>
              {['Cutting','Sewing','Washing','Finishing','Packing'].map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', gap:3, background:'#f1f5f9', padding:3, borderRadius:8 }}>
            {['day','week','month'].map(g=>(
              <button key={g} onClick={()=>setGran(g)}
                style={{ padding:'5px 11px', border:'none', borderRadius:6, cursor:'pointer',
                  fontSize:11, fontWeight:800, textTransform:'capitalize',
                  background: gran===g ? T.primary : 'transparent',
                  color: gran===g ? 'white' : T.textSub }}>{g}</button>
            ))}
          </div>
          <span style={{ marginLeft:'auto', fontSize:10, fontWeight:700, padding:'3px 9px',
            borderRadius:10, background: hasData?`${T.success}18`:`${T.warning}18`,
            color: hasData ? T.success : T.warning, whiteSpace:'nowrap' }}>
            {hasData ? `● ${series.length} periods · ${K.inspDone} inspections`
                     : '○ No data in window'}
          </span>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <FSel label="Season"   value={flt.season}   options={opts.season}   onChange={v=>setF('season',v)} />
          <FSel label="Brand"    value={flt.brand}    options={opts.brand}    onChange={v=>setF('brand',v)} />
          <FSel label="Region"   value={flt.region}   options={opts.region}   onChange={v=>setF('region',v)} />
          <FSel label="Supplier" value={flt.supplier} options={opts.supplier} onChange={v=>setF('supplier',v)} />
          <FSel label="Product"  value={flt.product}  options={opts.product}  onChange={v=>setF('product',v)} />
          <FSel label="QA Member" value={flt.qa}      options={opts.qa}       onChange={v=>setF('qa',v)} />
          <button onClick={resetAll} style={{ padding:'6px 14px', borderRadius:6,
            border:`1.5px solid ${T.border}`, background:'white', fontSize:11, fontWeight:800,
            cursor:'pointer', color:T.textSub }}>⟲ RESET ALL</button>
        </div>
      </div>

      {/* ─── MY KPIs ─── */}
      {myKpis.length>0 && (
        <Section title={`My KPIs — ${currentUser.name}`}>
          <CardRow min={180} mb={0}>
            {myKpis.map((k,i)=><StatCard key={k} label={k} value={kpiValue(k)} color={CC[i%CC.length]} />)}
          </CardRow>
        </Section>
      )}

      {/* ─── HEADLINE KPIs ─── */}
      <CardRow min={195}>
        <KpiTile label="First-Pass Rate" value={`${K.passRate}%`} trend={trends.passRate}
          sub={`${K.inspDone} inspections · ${K.inspSched} scheduled`} color={T.success} />
        <KpiTile label="DHU" value={K.dhu} trend={trends.dhu} invert suffix=""
          sub={`${K.defects.toLocaleString()} defects per ${K.checked.toLocaleString()} units checked`} color={T.teal} />
        <KpiTile label={`Output — ${outStage}`} value={K.produced.toLocaleString()} trend={trends.produced}
          sub={`pcs completed at ${outStage.toLowerCase()}`} color={T.primary} />
        <KpiTile label="Inspection Coverage" value={K.coverage !== null ? `${K.coverage}%` : '—'}
          sub={`${K.checked.toLocaleString()} checked of ${K.produced.toLocaleString()} produced`} color={T.accent} />
        <KpiTile label="Exceptions" value={K.excRaised} trend={trends.exceptions} invert
          sub={`${K.excApproved} approved · ${K.excPending} pending`} color={T.warning} />
        <KpiTile label="Commercial Decisions"
          value={K.cdTotal}
          sub={`${K.cdPassed} passed · ${K.cdPending} pending · ${K.cdEscalated} with merchant`}
          color="#dc2626" />
        <KpiTile label="CD Order Value"
          value={K.cdValue ? `₹${(K.cdValue/100000).toFixed(1)}L` : '₹0'}
          sub={`Value of commercially approved orders`}
          color="#9f1239" />
        <KpiTile label="Audit Average" value={K.auditAvg !== null ? `${K.auditAvg}%` : '—'}
          sub={`${D.audits.length} audits · grade avg ${K.auditGrade ?? '—'}`} color={T.purple} />
        <KpiTile label="CAP Closure" value={`${K.capClosure}%`} sub={`${K.capTotal} CAPs raised`} color={T.accent} />
        <KpiTile label="Customer Complaints" value={K.complaints}
          sub={`$${K.complaintClaim.toLocaleString()} claimed`} color={T.orange} />
        <KpiTile label="Overdue Plan Steps" value={K.overdueSteps} sub="mandatory, past schedule"
          color={K.overdueSteps ? T.danger : T.success} />
      </CardRow>

      {/* ─── MARKET-STYLE MAIN CHART ─── */}
      <Section title="Quality Performance Over Time — drag the handles below the chart to zoom the window">
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={series} margin={{ top:10, right:16, left:0, bottom:0 }}>
            <defs>
              <linearGradient id="gPass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={T.success} stopOpacity={0.35} />
                <stop offset="100%" stopColor={T.success} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="gProd" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={T.primary} stopOpacity={0.28} />
                <stop offset="100%" stopColor={T.primary} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
            <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={{ stroke:'#e6e9ef' }} />
            <YAxis yAxisId="left" tick={axis} tickLine={false} axisLine={false}
              label={{ value:'Pass rate %', angle:-90, position:'insideLeft', style:axis }} domain={[0,100]} />
            <YAxis yAxisId="right" orientation="right" tick={axis} tickLine={false} axisLine={false}
              label={{ value:'Qty', angle:90, position:'insideRight', style:axis }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize:11 }} />
            <ReferenceLine yAxisId="left" y={95} stroke={T.success} strokeDasharray="4 4"
              label={{ value:'target 95%', fontSize:9, fill:T.success, position:'right' }} />
            <Area yAxisId="right" type="monotone" dataKey="produced" name={`Output — ${outStage}`}
              stroke={T.primary} fill="url(#gProd)" strokeWidth={2} />
            <Area yAxisId="left" type="monotone" dataKey="passRate" name="Pass Rate %"
              stroke={T.success} fill="url(#gPass)" strokeWidth={2.5} />
            <Bar yAxisId="right" dataKey="defects" name="Defects found" fill={T.danger}
              opacity={0.55} radius={[3,3,0,0]} barSize={14} />
            <Line yAxisId="left" type="monotone" dataKey="dhu" name="DHU (defects per 100 units)" stroke={T.warning}
              strokeWidth={2} dot={false} />
            <Brush dataKey="label" height={22} stroke={T.primary} travellerWidth={9}
              fill="#f8fafc" />
          </ComposedChart>
        </ResponsiveContainer>
      </Section>

      <Split min={430}>
        <Section title={`Quality vs Production — output measured at ${outStage}`}>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
              <XAxis dataKey="label" tick={axis} tickLine={false} />
              <YAxis yAxisId="l" tick={axis} tickLine={false} axisLine={false} />
              <YAxis yAxisId="r" orientation="right" tick={axis} tickLine={false} axisLine={false} domain={[0,100]} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize:11 }} />
              <Bar yAxisId="l" dataKey="produced" name={`Output — ${outStage}`} fill={T.primary} radius={[3,3,0,0]} />
              <Bar yAxisId="l" dataKey="checked"  name="Units inspected" fill={T.teal} radius={[3,3,0,0]} />
              <Line yAxisId="r" type="monotone" dataKey="passRate" name="Pass Rate %"
                stroke={T.success} strokeWidth={2.5} dot={{ r:2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Section>

        <Section title="Defect Severity Trend">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={series} stackOffset="none">
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
              <XAxis dataKey="label" tick={axis} tickLine={false} />
              <YAxis tick={axis} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize:11 }} />
              <Area type="monotone" dataKey="critical" name="Critical" stackId="1"
                stroke={T.danger} fill={T.danger} fillOpacity={0.75} />
              <Area type="monotone" dataKey="major" name="Major" stackId="1"
                stroke={T.warning} fill={T.warning} fillOpacity={0.65} />
              <Area type="monotone" dataKey="minor" name="Minor" stackId="1"
                stroke={T.primary} fill={T.primary} fillOpacity={0.45} />
            </AreaChart>
          </ResponsiveContainer>
        </Section>
      </Split>

      <Split min={430}>
        <Section title="Supplier Performance in Window">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={supplierPerf.slice(0,12)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
              <XAxis dataKey="name" tick={axis} tickLine={false} interval={0} angle={-18} textAnchor="end" height={54} />
              <YAxis tick={axis} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize:11 }} />
              <Bar dataKey="passRate"     name="Pass Rate %" fill={T.success} radius={[3,3,0,0]} />
              <Bar dataKey="majorDefects" name="Cri+Maj Defects" fill={T.danger} radius={[3,3,0,0]} />
              <Bar dataKey="exceptions"   name="Exceptions" fill={T.warning} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        <Section title="QA Team Performance">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={teamPerf.slice(0,12)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
              <XAxis dataKey="name" tick={axis} tickLine={false} interval={0} angle={-18} textAnchor="end" height={54} />
              <YAxis tick={axis} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize:11 }} />
              <Bar dataKey="done"       name="Inspections" fill={T.primary} radius={[3,3,0,0]} />
              <Bar dataKey="passed"     name="Passed"      fill={T.success} radius={[3,3,0,0]} />
              <Bar dataKey="exceptions" name="Exceptions"  fill={T.warning} radius={[3,3,0,0]} />
              <Bar dataKey="visits"     name="Visits Done" fill={T.purple} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>
      </Split>

      <Split min={380}>
        <Section title="Inspection Results">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={resultPie} dataKey="value" nameKey="name" innerRadius={52} outerRadius={88}
                paddingAngle={2} label={e=>`${e.name}: ${e.value}`} labelLine={false}>
                {resultPie.map((e,i)=>(
                  <Cell key={i} fill={{ Passed:T.success, Failed:T.danger, Scheduled:T.warning }[e.name]||CC[i]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </Section>

        <Section title="Top Defect Codes in Window">
          {defectMix.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={defectMix} layout="vertical" margin={{ left:20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
                <XAxis type="number" tick={axis} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ ...axis, fontSize:9.5 }} width={110} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" name="Defects" fill={T.danger} radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{ fontSize:12, color:T.textSub, padding:20, textAlign:'center' }}>
            No defect codes recorded in this window.</div>}
        </Section>
      </Split>

      <Section title="Supplier League Table — window totals">
        <DataTable
          columns={['#','Supplier','Inspections','Pass Rate','Cri+Maj','Exceptions','Comm. Dec.','Audit Avg','Open CAPs','Complaints','Claim $',`Output (${outStage})`]}
          rows={supplierPerf.slice().sort((a,b)=>b.passRate-a.passRate).map((s,i)=>({ ...s, rank:i+1,
            cdCount: D.commercialDecisions.filter(c=>c.supplier===s.name).length,
          }))}
          renderCell={(r,c)=>({
            '#':<b>{r.rank}</b>, 'Supplier':<b>{r.full}</b>, 'Inspections':r.inspections,
            'Pass Rate':<b style={{ color: r.passRate>=90?T.success:r.passRate>=70?T.warning:T.danger }}>{r.passRate}%</b>,
            'Cri+Maj':r.majorDefects, 'Exceptions':r.exceptions,
            'Comm. Dec.': r.cdCount > 0
              ? <Tag label={String(r.cdCount)} color="#dc2626"/> : '—',
            'Audit Avg': r.auditScore !== null ? `${r.auditScore}%` : '—',
            'Open CAPs': r.capOpen ? <Tag label={String(r.capOpen)} color={T.warning}/> : '—',
            'Complaints': r.complaints || '—',
            'Claim $': r.claimAmount ? `$${r.claimAmount.toLocaleString()}` : '—',
            [`Output (${outStage})`]: r.produced.toLocaleString(),
          }[c])}
          emptyMsg="No supplier activity in the selected window and filters." />
      </Section>
    </Page>
  );
}
